"use client";

import { useEffect, useMemo, useState } from "react";
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useAppColors } from "@/styles/colors";

type LinePoint = {
  date: string;
  value: number;
};

/**
 * Token-resolvable keys for series colors. Prefer `colorKey` so the chart
 * resolves the color from the active theme (light/dark). `color` and
 * `darkColor` are kept as a fallback for backwards compatibility but
 * should be avoided in new code — see src/styles/colors/ for the full
 * token vocabulary.
 */
export type SeriesColorKey =
  | keyof ReturnType<typeof useAppColors>["charts"]["market"]
  | keyof ReturnType<typeof useAppColors>["semantic"];

type LineSeries = {
  key: string;
  label: string;
  /** Resolved color key (preferred). */
  colorKey?: SeriesColorKey;
  /** @deprecated Pass `colorKey` instead. Hex/RGB literal used in light mode. */
  color?: string;
  /** @deprecated Optional dark-mode override. Prefer `colorKey`. */
  darkColor?: string;
  points: LinePoint[];
  yAxis?: "left" | "right";
  tooltipPoints?: LinePoint[];
  tooltipValueFormat?: "number" | "currency_brl" | "percent";
  tooltipSuffix?: string;
};

type MarketLineChartProps = {
  title: string;
  subtitle?: string;
  helpTooltip?: string;
  series: LineSeries[];
  valueFormat?: "number" | "currency_brl" | "percent";
  valueSuffix?: string;
  normalizeToWindowStart?: boolean;
  defaultVisibleKeys?: string[];
  defaultRangeKey?: RangeOption["key"];
};

type RangeOption = {
  key: "3M" | "6M" | "1A" | "2A" | "5A" | "10A" | "MAX";
  days: number | null;
};

const RANGE_OPTIONS: RangeOption[] = [
  { key: "3M", days: 90 },
  { key: "6M", days: 180 },
  { key: "1A", days: 365 },
  { key: "2A", days: 365 * 2 },
  { key: "5A", days: 365 * 5 },
  { key: "10A", days: 365 * 10 },
  { key: "MAX", days: null },
];

const DATE_FORMATTER = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
});

const NUMBER_FORMATTER = new Intl.NumberFormat("pt-BR", {
  maximumFractionDigits: 2,
});
const CURRENCY_BRL_FORMATTER = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  maximumFractionDigits: 2,
});
const PERCENT_FORMATTER = new Intl.NumberFormat("pt-BR", {
  maximumFractionDigits: 2,
});

const chartWidth = 960;
const chartHeight = 320;
const left = 50;
const right = chartWidth - 16;
const top = 8;
const bottom = chartHeight - 14;

function buildLinePath(
  points: LinePoint[],
  minTs: number,
  maxTs: number,
  minValue: number,
  maxValue: number,
) {
  if (points.length === 0) return "";
  const valueRange = maxValue - minValue || 1;
  const tsRange = maxTs - minTs || 1;
  const chunks: string[] = [];
  points.forEach((point, index) => {
    const ts = new Date(point.date).getTime();
    const x = left + ((ts - minTs) / tsRange) * (right - left);
    const y = bottom - ((point.value - minValue) / valueRange) * (bottom - top);
    chunks.push(`${index === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`);
  });
  return chunks.join(" ");
}

function computeBounds(points: LinePoint[]) {
  if (points.length === 0) return null;
  const minValue = Math.min(...points.map((item) => item.value));
  const maxValue = Math.max(...points.map((item) => item.value));
  const yPad = Math.max(
    (maxValue - minValue) * 0.08,
    Math.abs(maxValue) * 0.02,
    1e-6,
  );
  return {
    min: minValue - yPad,
    max: maxValue + yPad,
  };
}

function getPointAsOf(points: LinePoint[], targetTs: number) {
  if (points.length === 0) return null;
  let best: LinePoint | null = null;
  for (const point of points) {
    const ts = new Date(point.date).getTime();
    if (ts <= targetTs) {
      best = point;
      continue;
    }
    break;
  }
  return best ?? points[0];
}

export default function MarketLineChart({
  title,
  subtitle,
  helpTooltip,
  series,
  valueFormat = "number",
  valueSuffix = "",
  normalizeToWindowStart = false,
  defaultVisibleKeys,
  defaultRangeKey = "1A",
}: MarketLineChartProps) {
  const theme = useTheme();
  const colors = useAppColors();
  const isLight = theme.palette.mode === "light";
  const rangeColors = colors.actions.range;
  const resolveSeriesColor = (
    line: Pick<LineSeries, "color" | "darkColor" | "colorKey">,
  ): string => {
    if (line.colorKey) {
      const { semantic, charts } = colors;
      if (line.colorKey in semantic) {
        return semantic[line.colorKey as keyof typeof semantic];
      }
      return charts.market[line.colorKey as keyof typeof charts.market];
    }
    return isLight ? (line.color ?? "") : (line.darkColor ?? line.color ?? "");
  };
  const formatValue = (
    value: number,
    format: "number" | "currency_brl" | "percent",
    suffix = "",
  ) => {
    if (format === "currency_brl") {
      return `${CURRENCY_BRL_FORMATTER.format(value)}${suffix}`;
    }
    if (format === "percent") {
      return `${PERCENT_FORMATTER.format(value)}%${suffix}`;
    }
    return `${NUMBER_FORMATTER.format(value)}${suffix}`;
  };

  const [rangeKey, setRangeKey] = useState<RangeOption["key"]>(defaultRangeKey);
  const latestTsGlobal = Math.max(
    ...series.flatMap((line) =>
      line.points.map((point) => new Date(point.date).getTime()),
    ),
  );
  const selectedRange =
    RANGE_OPTIONS.find((item) => item.key === rangeKey) ?? RANGE_OPTIONS[2];
  const hasCutoff =
    Number.isFinite(latestTsGlobal) && selectedRange.days !== null;
  const cutoffDays = selectedRange.days ?? 0;
  const cutoffTs = hasCutoff
    ? latestTsGlobal - cutoffDays * 24 * 60 * 60 * 1000
    : Number.NEGATIVE_INFINITY;
  const cutoffDate = hasCutoff
    ? new Date(cutoffTs).toISOString().slice(0, 10)
    : null;
  const filterWithAnchor = (points: LinePoint[]) => {
    if (points.length === 0) return points;
    if (!hasCutoff) return points;
    const filtered = points.filter(
      (point) => new Date(point.date).getTime() >= cutoffTs,
    );
    if (filtered.length === 0) return filtered;
    const firstFilteredTs = new Date(filtered[0].date).getTime();
    let anchor: LinePoint | null = null;
    for (let index = points.length - 1; index >= 0; index -= 1) {
      const ts = new Date(points[index].date).getTime();
      if (ts < firstFilteredTs && ts < cutoffTs) {
        anchor = points[index];
        break;
      }
    }
    return anchor && cutoffDate
      ? [{ date: cutoffDate, value: anchor.value }, ...filtered]
      : filtered;
  };
  const rangedSeries = series.map((line) => {
    const filtered = filterWithAnchor(line.points);
    const filteredTooltipPoints = filterWithAnchor(
      line.tooltipPoints ?? line.points,
    );
    return {
      ...line,
      points: filtered,
      tooltipPoints: filteredTooltipPoints,
    };
  });
  const chartSeries = normalizeToWindowStart
    ? rangedSeries.map((line) => {
        if (line.points.length === 0) return line;
        const base = line.points[0].value;
        return {
          ...line,
          points: line.points.map((point) => ({
            date: point.date,
            value: point.value - base,
          })),
          tooltipPoints: (line.tooltipPoints ?? []).map((point) => ({
            date: point.date,
            value: point.value - base,
          })),
        };
      })
    : rangedSeries;
  const legendSeries = chartSeries;
  const plottableSeries = chartSeries.filter((item) => item.points.length > 0);
  const defaultVisible = useMemo(() => {
    if (!defaultVisibleKeys || defaultVisibleKeys.length === 0) {
      return plottableSeries.map((item) => item.key);
    }
    const allowed = new Set(legendSeries.map((item) => item.key));
    const configured = defaultVisibleKeys.filter((key) => allowed.has(key));
    if (configured.length > 0) return configured;
    return plottableSeries.map((item) => item.key);
  }, [defaultVisibleKeys, legendSeries, plottableSeries]);
  const [visibleKeys, setVisibleKeys] = useState<string[]>(
    () => defaultVisible,
  );
  const defaultVisibleSignature = defaultVisible.join("|");

  useEffect(() => {
    setVisibleKeys((current) => {
      if (
        current.length === defaultVisible.length &&
        current.every((key, index) => key === defaultVisible[index])
      ) {
        return current;
      }
      return defaultVisible;
    });
  }, [rangeKey, defaultVisibleSignature]);

  const visibleSeries = useMemo(
    () => plottableSeries.filter((item) => visibleKeys.includes(item.key)),
    [plottableSeries, visibleKeys],
  );

  if (plottableSeries.length === 0) {
    return (
      <Box
        sx={{
          border: "1px dashed",
          borderColor: "divider",
          borderRadius: 2,
          p: 2,
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        ) : null}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Sem dados suficientes para exibir o gráfico.
        </Typography>
      </Box>
    );
  }

  const rangeSeries =
    visibleSeries.length > 0 ? visibleSeries : plottableSeries;
  const allPoints = rangeSeries.flatMap((item) => item.points);
  const minTs = Math.min(
    ...allPoints.map((item) => new Date(item.date).getTime()),
  );
  const maxTs = Math.max(
    ...allPoints.map((item) => new Date(item.date).getTime()),
  );
  const leftAxisSeries = rangeSeries.filter((item) => item.yAxis !== "right");
  const rightAxisSeries = rangeSeries.filter((item) => item.yAxis === "right");
  const fallbackSeries =
    leftAxisSeries.length > 0 ? leftAxisSeries : rightAxisSeries;
  const leftBounds = computeBounds(
    fallbackSeries.flatMap((item) => item.points),
  );
  const rightBounds = computeBounds(
    rightAxisSeries.flatMap((item) => item.points),
  );
  const yMin = leftBounds?.min ?? 0;
  const yMax = leftBounds?.max ?? 1;

  const midTs = minTs + (maxTs - minTs) / 2;
  const xLabels = [minTs, midTs, maxTs].map((ts) =>
    DATE_FORMATTER.format(new Date(ts)),
  );
  const horizontalGridYs = [0.2, 0.4, 0.6, 0.8].map(
    (ratio) => top + ratio * (bottom - top),
  );
  const timelineTs = useMemo(
    () =>
      Array.from(
        new Set(allPoints.map((item) => new Date(item.date).getTime())),
      ).sort((a, b) => a - b),
    [allPoints],
  );
  const [hoverTs, setHoverTs] = useState<number | null>(null);

  useEffect(() => {
    setHoverTs(null);
  }, [rangeKey, visibleKeys, series]);

  const hoverNearestTs = useMemo(() => {
    if (hoverTs === null || timelineTs.length === 0) return null;
    return timelineTs.reduce((best, ts) =>
      Math.abs(ts - hoverTs) < Math.abs(best - hoverTs) ? ts : best,
    );
  }, [hoverTs, timelineTs]);
  const xFromTs = (ts: number) =>
    left + ((ts - minTs) / (maxTs - minTs || 1)) * (right - left);
  const hoverX = hoverNearestTs !== null ? xFromTs(hoverNearestTs) : null;
  const hoverRows =
    hoverNearestTs !== null
      ? rangeSeries
          .map((line) => ({
            key: line.key,
            label: line.label,
            colorKey: line.colorKey,
            color: resolveSeriesColor(line),
            darkColor: resolveSeriesColor(line),
            point: getPointAsOf(
              line.tooltipPoints ?? line.points,
              hoverNearestTs,
            ),
            tooltipValueFormat: line.tooltipValueFormat ?? valueFormat,
            tooltipSuffix: line.tooltipSuffix ?? valueSuffix,
          }))
          .filter((item) => item.point !== null)
      : [];

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 2,
        bgcolor: isLight
          ? alpha(theme.palette.background.paper, 0.92)
          : "hsl(var(--card))",
      }}
    >
      <Stack
        direction="row"
        spacing={0.8}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="h6" fontWeight={800}>
          {title}
        </Typography>
        {helpTooltip ? (
          <Tooltip title={helpTooltip} arrow>
            <Box
              component="span"
              sx={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                border: "1px solid",
                borderColor: "divider",
                color: "text.secondary",
                fontSize: 12,
                fontWeight: 800,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 1,
                cursor: "help",
                userSelect: "none",
              }}
            >
              ?
            </Box>
          </Tooltip>
        ) : null}
      </Stack>
      {subtitle ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {subtitle}
        </Typography>
      ) : null}

      <Stack
        direction="row"
        spacing={0.8}
        sx={{ mt: 1.2 }}
        useFlexGap
        flexWrap="wrap"
      >
        {RANGE_OPTIONS.map((option) => (
          <Chip
            key={option.key}
            size="small"
            label={option.key}
            onClick={() => setRangeKey(option.key)}
            variant={rangeKey === option.key ? "filled" : "outlined"}
            sx={{
              fontWeight: 700,
              ...(rangeKey === option.key
                ? {
                    bgcolor: rangeColors.selectedBg,
                    color: rangeColors.selectedText,
                    borderColor: rangeColors.selectedBorder,
                    "&:hover": {
                      bgcolor: rangeColors.selectedHoverBg,
                      borderColor: rangeColors.selectedHoverBorder,
                    },
                  }
                : {
                    borderColor: rangeColors.unselectedBorder,
                    color: rangeColors.unselectedText,
                    "&:hover": {
                      color: rangeColors.unselectedHoverText,
                      borderColor: rangeColors.unselectedHoverBorder,
                      backgroundColor: rangeColors.unselectedHoverBg,
                    },
                  }),
            }}
          />
        ))}
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        sx={{ mt: 1.2 }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ alignSelf: "center", mr: 0.5 }}
        >
          Legenda:
        </Typography>
        {legendSeries.map((line) => {
          const active = visibleKeys.includes(line.key);
          return (
            <Chip
              key={line.key}
              label={line.label}
              onClick={() =>
                setVisibleKeys((current) =>
                  current.includes(line.key)
                    ? current.filter((key) => key !== line.key)
                    : [...current, line.key],
                )
              }
              variant={active ? "filled" : "outlined"}
              sx={{
                borderColor: active
                  ? resolveSeriesColor(line)
                  : alpha(theme.palette.text.primary, 0.22),
                bgcolor: active
                  ? alpha(resolveSeriesColor(line), isLight ? 0.12 : 0.24)
                  : "transparent",
                color: active
                  ? resolveSeriesColor(line)
                  : theme.palette.text.secondary,
                fontWeight: 700,
                "& .MuiChip-label": { px: 1.1 },
              }}
            />
          );
        })}
      </Stack>

      <Box sx={{ mt: 1.5 }}>
        <Box
          sx={{
            mb: 0.8,
            p: 1,
            minHeight: 78,
            borderRadius: 1,
            border: "1px solid",
            borderColor: hoverNearestTs !== null ? "divider" : "transparent",
            bgcolor:
              hoverNearestTs !== null
                ? isLight
                  ? alpha(theme.palette.background.default, 0.92)
                  : "hsl(var(--foreground) / 0.06)"
                : "transparent",
          }}
        >
          {hoverNearestTs !== null ? (
            <>
              <Typography variant="caption" color="text.secondary">
                {DATE_FORMATTER.format(new Date(hoverNearestTs))}
              </Typography>
              <Stack
                direction="row"
                spacing={1.2}
                useFlexGap
                flexWrap="wrap"
                sx={{ mt: 0.4 }}
              >
                {hoverRows.map((row) => (
                  <Typography
                    key={row.key}
                    variant="caption"
                    sx={{ color: resolveSeriesColor(row), fontWeight: 700 }}
                  >
                    {row.label}:{" "}
                    {formatValue(
                      row.point!.value,
                      row.tooltipValueFormat,
                      row.tooltipSuffix,
                    )}
                  </Typography>
                ))}
              </Stack>
            </>
          ) : null}
        </Box>

        <svg
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          width="100%"
          role="img"
          aria-label={title}
          onMouseMove={(event) => {
            const rect = event.currentTarget.getBoundingClientRect();
            const px = event.clientX - rect.left;
            const svgX = (px / rect.width) * chartWidth;
            const clamped = Math.min(right, Math.max(left, svgX));
            const ts =
              minTs +
              ((clamped - left) / (right - left || 1)) * (maxTs - minTs);
            setHoverTs(ts);
          }}
          onMouseLeave={() => setHoverTs(null)}
        >
          {horizontalGridYs.map((y) => (
            <line
              key={`grid-${y}`}
              x1={left}
              y1={y}
              x2={right}
              y2={y}
              stroke={
                isLight
                  ? alpha(theme.palette.text.primary, 0.1)
                  : "hsl(var(--muted-foreground) / 0.20)"
              }
              strokeWidth="1"
            />
          ))}
          <line
            x1={left}
            y1={top}
            x2={left}
            y2={bottom}
            stroke={
              isLight
                ? alpha(theme.palette.text.primary, 0.26)
                : "hsl(var(--muted-foreground) / 0.28)"
            }
            strokeWidth="1"
          />
          {rightBounds ? (
            <line
              x1={right}
              y1={top}
              x2={right}
              y2={bottom}
              stroke={
                isLight
                  ? alpha(theme.palette.text.primary, 0.26)
                  : "hsl(var(--muted-foreground) / 0.28)"
              }
              strokeWidth="1"
            />
          ) : null}
          <line
            x1={left}
            y1={bottom}
            x2={right}
            y2={bottom}
            stroke={
              isLight
                ? alpha(theme.palette.text.primary, 0.26)
                : "hsl(var(--muted-foreground) / 0.28)"
            }
            strokeWidth="1"
          />
          {hoverX !== null ? (
            <line
              x1={hoverX}
              y1={top}
              x2={hoverX}
              y2={bottom}
              stroke={
                isLight
                  ? alpha(theme.palette.text.primary, 0.32)
                  : "hsl(var(--foreground) / 0.24)"
              }
              strokeWidth="1"
              strokeDasharray="3 3"
            />
          ) : null}
          {visibleSeries.map((line) => (
            <path
              key={line.key}
              d={buildLinePath(
                line.points,
                minTs,
                maxTs,
                line.yAxis === "right" && rightBounds ? rightBounds.min : yMin,
                line.yAxis === "right" && rightBounds ? rightBounds.max : yMax,
              )}
              fill="none"
              stroke={resolveSeriesColor(line)}
              strokeWidth="2.4"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}
        </svg>
      </Box>

      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ mt: -0.5, px: 0.5 }}
      >
        {xLabels.map((item, index) => (
          <Typography
            key={`${item}-${index}`}
            variant="caption"
            color="text.secondary"
          >
            {item}
          </Typography>
        ))}
      </Stack>

      {visibleSeries.length === 0 ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: "block" }}
        >
          Selecione ao menos uma série na legenda.
        </Typography>
      ) : null}
    </Box>
  );
}
